/**
 * Тест, который ученик не сможет сдать: у вопроса не отмечен верный ответ.
 *
 * ⚠️ ЗАЧЕМ. Сервер проверки (progress-service, `field_grader.go`) считает весь блок
 * непроверяемым, если хоть у одного вопроса нет верного варианта: ученик отвечает, а
 * задание не засчитывается, как бы он ни ответил. С 12.09.2026 такой блок не даёт
 * сохранить и сам courses-service — `400 quiz_no_correct_answer`. Здесь то же правило
 * во фронте, чтобы методист увидел беду ДО сохранения, а не в виде отказа.
 *
 * ⚠️ ПРАВИЛО ДОСЛОВНО КАК У СЕРВЕРА — `services/courses-service/internal/service/
 * block_content_guard.go` (`проверитьКвиз`, `верныеВарианты`, `проверитьВерноНеверно`),
 * договор `docs/CONTRACTS/constructor-v2.md` §1.2 и §1.3. Мягче — промолчим там, где
 * сервер откажет, и отказ свалится как гром; строже — будем ругаться на годное задание,
 * и методист перестанет читать подсказку вовсе. Поменяли там — меняйте здесь.
 *
 * ⚠️ ПУСТАЯ ЗАГОТОВКА ПРОХОДИТ. Автосохранение конструктора отправляет только что
 * добавленный блок через 2,5 с, ещё до того, как в нём что-то набрали.
 *
 * ⚠️ ПОДСКАЗКА НЕ ЗАПРЕЩАЕТ СОХРАНЕНИЕ. Запрет держит сервер, и он намеренно
 * пропускает неизменённый старый тест (§1.4) — иначе урок со старым битым тестом
 * нельзя было бы сохранить вовсе, даже правя в нём опечатку.
 */

/** Разбор одного блока: чего не хватает, чтобы задание было проходимым. */
export type BlockGuardIssue =
  | { kind: 'quiz_no_correct'; indexes: number[] }
  | { kind: 'tf_foreign_answer'; indexes: number[] };

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * Верные варианты вопроса — близнец `верныеВарианты` сервера.
 *
 * ⚠️ `correct` должен быть ЛОГИЧЕСКИМ `true`. Строка `"true"` верной не считается:
 * сервер проверки её не примет, и задание останется несдаваемым.
 */
function correctOptions(q: Record<string, unknown>): number[] {
  const raw = q.options;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (isObject(raw[0])) {
    const out: number[] = [];
    raw.forEach((o, i) => {
      if (isObject(o) && o.correct === true) out.push(i);
    });
    return out;
  }
  // Старый вид: варианты строками, верный — номером в `answer`.
  const a = q.answer;
  if (typeof a === 'number' && Number.isInteger(a) && a >= 0 && a < raw.length) return [a];
  return [];
}

/** Вопрос заполнен: есть текст вопроса или текст хотя бы одного варианта. */
function questionFilled(q: Record<string, unknown>): boolean {
  if (text(q.text) !== '' || text(q.question) !== '') return true;
  const raw = q.options;
  if (!Array.isArray(raw)) return false;
  return raw.some((o) => (isObject(o) ? text(o.text) !== '' : text(o) !== ''));
}

/** Вопросы блока: непустой `questions`, иначе само содержимое — один плоский вопрос. */
function questionsOf(c: Record<string, unknown>): unknown[] {
  const qs = c.questions;
  return Array.isArray(qs) && qs.length > 0 ? qs : [c];
}

function checkQuiz(c: Record<string, unknown>): BlockGuardIssue | null {
  const questions = questionsOf(c);
  // Заготовка, в которой ничего не набрано, проходит целиком.
  const anyFilled = questions.some((q) => !isObject(q) || questionFilled(q));
  if (!anyFilled) return null;

  // ⚠️ ВЕРНЫЙ ОТВЕТ НУЖЕН У КАЖДОГО ВОПРОСА, в том числе у пустого внутри
  // заполненного теста: одного такого хватает, чтобы блок стал непроверяемым.
  const indexes: number[] = [];
  questions.forEach((q, i) => {
    if (!isObject(q) || correctOptions(q).length === 0) indexes.push(i);
  });
  return indexes.length > 0 ? { kind: 'quiz_no_correct', indexes } : null;
}

const TF_ANSWERS = ['', 'true', 'false', 'not_given'];

function checkTrueFalse(c: Record<string, unknown>): BlockGuardIssue | null {
  const raw = c.statements;
  const statements = Array.isArray(raw) && raw.length > 0 ? raw : [c];
  // Вариант «True / False» (`not_given: false`): ответ not_given ученику не выбрать.
  const allowed = c.not_given === false ? TF_ANSWERS.filter((a) => a !== 'not_given') : TF_ANSWERS;
  const indexes: number[] = [];
  statements.forEach((it, i) => {
    if (!isObject(it)) {
      indexes.push(i);
      return;
    }
    const v = it.answer;
    // Пусто — это «Верно» по правилу сервера проверки, отказом не считается.
    if (v === undefined || v === null) return;
    if (typeof v !== 'string' || !allowed.includes(v)) indexes.push(i);
  });
  return indexes.length > 0 ? { kind: 'tf_foreign_answer', indexes } : null;
}

/**
 * Что сервер не примет в этом блоке. `null` — примет (или тип сторожем не проверяется:
 * `mc_*` живут только в пуле IELTS, в уроках их нет).
 */
export function blockGuardIssue(type: string, content: unknown): BlockGuardIssue | null {
  if (!isObject(content)) return null;
  switch (type) {
    case 'quiz_no_timer':
    case 'quiz_with_timer':
      return checkQuiz(content);
    case 'true_false_ng':
      return checkTrueFalse(content);
    default:
      return null;
  }
}

/** Номера для человека: с единицы, не больше пяти, дальше «…». */
export function issueNumbers(indexes: number[], limit = 5): string {
  const shown = indexes.slice(0, limit).map((n) => String(n + 1));
  if (indexes.length > limit) shown.push('…');
  return shown.join(', ');
}
